import { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../../lib/api/client.js';
import { seatNumberFromSipUser, type LiveExtension } from '@arcnet/contracts';
import { useLiveScope, useLiveState } from './useLiveState.js';
import { useRealtime } from '../../lib/api/realtime.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, Stat, StatGroup, Segmented } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { isUnreachable, pingDetail } from '../extensions/ExtensionPresence.js';
import { TeamsStatusCell } from '../manage/TeamsPresence.js';
import { relativeTime, formatDuration } from '../../lib/format.js';

/**
 * Every seat on the platform, live.
 *
 * The operator's view rather than a customer's: one table across every account,
 * sorted so anything wrong floats to the top. It exists because "is anything
 * broken right now" is a question nobody should have to answer by opening
 * thirty accounts in turn.
 *
 * Updates arrive on the live stream: a phone registering anywhere refreshes
 * this within about a second, and a call shows the moment it rings.
 */

/** A row of /v1/live/extensions: the seat, not its call state. */
interface LiveSeat {
  id: string; number: string; display_name: string; status: string;
  do_not_disturb: boolean;
  tenant_id: string; tenant_name: string; sip_domain: string;
  admin_group_name: string | null;
  registered: boolean;
  contact_ip: string | null; contact_port: number | null;
  transport: string | null; user_agent: string | null;
  ping_status?: string | null; ping_ms?: number | null;
  last_seen_at: string | null; offline_since: string | null;
  ever_seen: boolean;
  /** Rings in Microsoft Teams (docs/44): whether its account is connected, and its Teams status. */
  teams?: { connected: boolean; presence: { availability: string; activity: string; at: string } | null } | null;
}

/**
 * A Teams seat counts as a phone (owner, 21 October 2026): signed in to Teams is online, signed out
 * is offline rather than never seen. Unknown (Microsoft 365 not connected) leaves it to its phones.
 */
const teamsIn = (e: LiveSeat): boolean | null => {
  const p = e.teams?.presence;
  if (!e.teams?.connected || !p) return null;
  return p.availability !== 'Offline' && p.availability !== 'PresenceUnknown';
};
/** Registered and answering pings, or signed in to Teams: reachable by a caller (ExtensionPresence.tsx). */
const isOnline = (e: LiveSeat): boolean => (e.registered && !isUnreachable(e.ping_status)) || teamsIn(e) === true;
const isOffline = (e: LiveSeat): boolean => !isOnline(e)
  && ((e.ever_seen && !e.registered) || (e.registered && isUnreachable(e.ping_status)) || teamsIn(e) === false);
const isNever = (e: LiveSeat): boolean => !isOnline(e) && !isOffline(e);

type Filter = 'active' | 'all' | 'online' | 'offline' | 'never';

/**
 * Call state where there is any, presence where there is not.
 *
 * "On a call" implies registered, so showing both would spend a column saying
 * the same thing twice.
 */
function CallBadge({ live, registered, everSeen, unreachable, detail }: {
  live: LiveExtension | null; registered: boolean; everSeen: boolean;
  unreachable: boolean; detail: string;
}) {
  if (live?.state === 'on_call') {
    return (
      <Badge tone="info">
        <span className="arc-dot" aria-hidden="true" />
        on a call{live.direction ? ` · ${live.direction}` : ''}
      </Badge>
    );
  }
  if (live?.state === 'ringing') {
    return (
      <Badge tone="warn">
        <span className="arc-dot arc-pulse" aria-hidden="true" />ringing
      </Badge>
    );
  }
  if (registered && unreachable) {
    return <Badge tone="danger"><span className="arc-dot" aria-hidden="true" />unreachable</Badge>;
  }
  if (registered) {
    return (
      <>
        <Badge tone="ok"><span className="arc-dot" aria-hidden="true" />online</Badge>
        {detail && <span className="arc-subtle arc-num" style={{ fontSize: 11.5, marginLeft: 6 }}>{detail}</span>}
      </>
    );
  }
  if (everSeen) {
    return <Badge tone="danger"><span className="arc-dot" aria-hidden="true" />offline</Badge>;
  }
  return <Badge tone="muted">never seen</Badge>;
}

export function LivePage() {
  const nav = useNavigate();
  // Who is on a call or ringing, first: what this page is for (UI review, 3 October 2026). The
  // other filters remain for the rest.
  const [filter, setFilter] = useState<Filter>('active');
  const [q, setQ] = useState('');

  /**
   * Call state from the calls channel, presence from the registrations one.
   * Both ride the tab's single socket; the queues channel is not asked for,
   * because nothing on this page shows a queue.
   */
  const { platform, tenantId } = useLiveScope();
  const { extensions: liveExtensions, connection: live } = useLiveState(['calls']);
  useRealtime(tenantId, { platform });

  // By seat: a channel is named by its SIP login (1001-meridian), a seat by its number. Keyed by the
  // login, no call was ever matched to its seat here.
  const callByExt = useMemo(() => {
    const m = new Map<string, LiveExtension>();
    for (const e of liveExtensions) m.set(`${seatNumberFromSipUser(e.user, e.domain) ?? e.user}@${e.domain}`, e);
    return m;
  }, [liveExtensions]);
  const callOf = (e: LiveSeat) => callByExt.get(`${e.number}@${e.sip_domain}`) ?? null;

  // A one-second clock while anybody is on a call, for the call timers.
  const anyCall = liveExtensions.some((e) => e.state === 'on_call' || e.state === 'ringing');
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!anyCall) return;
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [anyCall]);

  const { data, isLoading, error } = useQuery({
    // One account when there is one to show; the API checks it is in the caller's scope.
    queryKey: ['live-extensions', tenantId],
    queryFn: () => api.get<{ data: LiveSeat[] }>(`/v1/live/extensions${tenantId ? `?tenant_id=${tenantId}` : ''}`),
    // A safety net only. The event stream is what keeps this current; without
    // the net, a dropped stream would leave the page silently frozen.
    refetchInterval: 60_000,
  });

  const all = data?.data ?? [];
  const counts = useMemo(() => ({
    total: all.length,
    onCall: all.filter((e) => callOf(e)?.state === 'on_call').length,
    ringing: all.filter((e) => callOf(e)?.state === 'ringing').length,
    online: all.filter(isOnline).length,
    offline: all.filter(isOffline).length,
    never: all.filter(isNever).length,
    tenants: new Set(all.map((e) => e.tenant_id)).size,
  }), [all, callByExt]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((e) => {
      if (filter === 'active' && !callOf(e)) return false;
      if (filter === 'online' && !isOnline(e)) return false;
      if (filter === 'offline' && !isOffline(e)) return false;
      if (filter === 'never' && !isNever(e)) return false;
      if (!needle) return true;
      return e.number.includes(needle)
        || e.display_name.toLowerCase().includes(needle)
        || e.tenant_name.toLowerCase().includes(needle)
        || (e.user_agent ?? '').toLowerCase().includes(needle)
        || (e.contact_ip ?? '').includes(needle);
    });
  }, [all, filter, q, callByExt]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">{platform ? 'Live' : 'Live extensions'}</h1>
          <p>Who is on a call or ringing, with whom and for how long{platform ? ', across the platform' : ''}.</p>
        </div>
        <div className="arc-row">
          <Badge tone={live === 'live' ? 'ok' : 'warn'}>
            <span className="arc-dot" aria-hidden="true" />
            {live === 'live' ? 'live' : live === 'connecting' ? 'connecting' : 'reconnecting'}
          </Badge>
        </div>
      </div>

      {error && <ErrorBanner error={error} />}

      <div style={{ marginBottom: 14 }}>
        <StatGroup>
          <Stat bare icon="phone" hue={1} label="On a call" value={counts.onCall}
            sub={platform ? `across ${counts.tenants} account${counts.tenants === 1 ? '' : 's'}` : 'in this account'} />
          <Stat bare icon="activity" hue={2} label="Ringing" value={counts.ringing} sub="not answered yet" />
          <Stat bare icon="extension" hue={4} label="Online" value={counts.online} sub={`of ${counts.total} seats`} />
          <Stat bare icon="warning" hue={3} label="Offline" value={counts.offline}
            tone={counts.offline > 0 ? 'warn' : undefined}
            sub={counts.offline > 0 ? 'was registered, now gone' : 'nothing down'} />
        </StatGroup>
      </div>

      {/* The queues table that used to sit here now has its own page under
          Realtime. It was a secondary card beneath a heading that said "every
          extension on the platform", which is not where anyone looks to find
          out whether callers are on hold. */}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Search account, extension, handset or IP"
              value={q} onChange={(e) => setQ(e.target.value)}
              aria-label="Search live extensions" />
          </div>
          <Segmented label="Show" value={filter} onChange={setFilter}
            options={[['active', 'On a call or ringing', counts.onCall + counts.ringing], ['all', 'All', counts.total], ['online', 'Online', counts.online],
              ['offline', 'Offline', counts.offline], ['never', 'Never seen', counts.never]] as const} />
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {rows.length === all.length ? all.length : `${rows.length} of ${all.length}`} shown
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                {platform && <th>Account</th>}<th>Extension</th><th>State</th><th>Call</th>
                <th>Handset</th><th>Address</th><th>Last seen</th>
              </tr>
            </thead>
            {isLoading ? <TableSkeleton cols={platform ? 7 : 6} /> : (
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="arc-clickable"
                    onClick={() => {
                      // Drills into the seat inside its own account, which is
                      // where every setting and the full registration history
                      // already live.
                      void nav({
                        to: '/manage/$tenantId/$',
                        params: { tenantId: e.tenant_id, _splat: `extensions/${e.number}` },
                      });
                    }}>
                    {platform && <td style={{ fontWeight: 550 }}>{e.tenant_name}</td>}
                    <td>
                      <div className="arc-row" style={{ gap: 10 }}>
                        <span className="arc-ext-num">{e.number}</span>
                        {e.teams && (
                          <span className="arc-muted" title="Microsoft Teams" aria-label="Microsoft Teams"
                            style={{ display: 'inline-flex', marginLeft: -4 }}>
                            <Icon name="teams" size={15} />
                          </span>
                        )}
                        <span className="arc-truncate">{e.display_name}</span>
                      </div>
                    </td>
                    <td>
                      {/* A Teams seat with no phone of its own shows its Teams status where a
                          phone's would be (owner, 21 October 2026). */}
                      {e.teams && !e.registered && !callOf(e) ? (
                        <TeamsStatusCell connected={e.teams.connected} presence={e.teams.presence} />
                      ) : (
                      <CallBadge
                        live={callOf(e)}
                        registered={e.registered} everSeen={e.ever_seen}
                        unreachable={isUnreachable(e.ping_status)}
                        detail={pingDetail(e.ping_status, e.ping_ms)} />
                      )}
                      {/* The same gap as the ping time before it. */}
                      {e.do_not_disturb && <span style={{ marginLeft: 6 }}><Badge tone="warn">DND</Badge></span>}
                    </td>
                    {/* The other party and how long: what a supervisor glances at. */}
                    <td className="arc-num">
                      {(() => {
                        const c = callOf(e);
                        if (!c) return <span className="arc-subtle">—</span>;
                        const arrow = c.direction === 'inbound' ? 'callIn' : c.direction === 'outbound' ? 'callOut' : 'callInternal';
                        return (
                          <span className="arc-row" style={{ gap: 6 }}>
                            <Icon name={arrow} size={12} />
                            <span className="arc-mono">{c.peer ?? ''}</span>
                            <span className="arc-subtle">{formatDuration(Math.max(0, Math.floor((now - c.since) / 1000)))}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="arc-muted arc-truncate" style={{ maxWidth: 190 }}>
                      {e.user_agent ?? <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-mono arc-muted" style={{ fontSize: 11.5 }}>
                      {e.contact_ip
                        ? `${e.contact_ip}${e.contact_port ? ':' + e.contact_port : ''}`
                        : <span className="arc-subtle">—</span>}
                      {e.transport && (
                        <span className="arc-subtle" style={{ marginLeft: 6 }}>
                          {e.transport.toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td className="arc-muted">
                      {e.last_seen_at
                        ? relativeTime(e.last_seen_at)
                        : <span className="arc-subtle">never</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {data !== undefined && all.length === 0 && (
          <Empty icon="activity" title="No extensions yet"
            hint="Seats appear here as soon as an account has one." />
        )}
        {data !== undefined && all.length > 0 && rows.length === 0 && (
          filter === 'active'
            ? <Empty icon="phone" title="Nobody is on a call" hint="Calls show here the moment one rings." />
            : <Empty icon="search" title="Nothing matches" hint="Try a different filter or search term." />
        )}
      </div>
    </>
  );
}
